// src/pages/AdminPage/PoliciesTab.tsx
import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Switch,
  Button,
  Drawer,
  TextField,
  CircularProgress,
  Skeleton,
} from '@mui/material';
import { ShieldCheck, Plus, X, Pencil, Trash2 } from 'lucide-react';
import {
  listPolicies,
  savePolicy,
  setPolicyEnabled,
  deletePolicy,
  type CompanyPolicy,
} from 'services/demoApi/policiesApi';

function formatUpdatedAt(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' });
}

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    fontSize: 13,
    '& fieldset': { borderColor: 'var(--color-border)' },
    '&.Mui-focused fieldset': { borderColor: 'var(--color-primary)' },
  },
  '& .MuiInputLabel-root': { fontSize: 13 },
};

export default function PoliciesTab() {
  const [policies, setPolicies] = useState<CompanyPolicy[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [prompt, setPrompt] = useState('');
  const [saving, setSaving] = useState(false);

  const refresh = () =>
    listPolicies().then((items) => {
      setPolicies(items);
      setIsLoading(false);
    });

  useEffect(() => {
    void refresh();
  }, []);

  const toggle = async (policy: CompanyPolicy) => {
    setPolicies((prev) =>
      prev.map((p) => (p.id === policy.id ? { ...p, enabled: !p.enabled } : p))
    );
    await setPolicyEnabled(policy.id, !policy.enabled);
  };

  const openCreate = () => {
    setEditingId(null);
    setTitle('');
    setDescription('');
    setPrompt('');
    setDrawerOpen(true);
  };

  const openEdit = (policy: CompanyPolicy) => {
    setEditingId(policy.id);
    setTitle(policy.title);
    setDescription(policy.description);
    setPrompt(policy.prompt);
    setDrawerOpen(true);
  };

  const submit = async () => {
    if (!title.trim() || !prompt.trim()) return;
    setSaving(true);
    try {
      await savePolicy(
        { title: title.trim(), description: description.trim(), prompt: prompt.trim() },
        editingId ?? undefined
      );
      await refresh();
      setDrawerOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (policy: CompanyPolicy) => {
    await deletePolicy(policy.id);
    await refresh();
  };

  return (
    <Box>
      {/* Инфо-баннер (FR-5.3) */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.5,
          mb: 2.5,
          bgcolor: 'rgba(20,116,255,0.06)',
          border: '1px solid rgba(20,116,255,0.14)',
          borderRadius: 'var(--radius-md)',
        }}
      >
        <ShieldCheck size={18} style={{ color: 'var(--color-primary)', flexShrink: 0 }} />
        <Typography sx={{ fontSize: 12.5, color: 'var(--color-text-secondary)' }}>
          Активные политики автоматически добавляются к каждому запросу пользователя и имеют
          приоритет над его инструкциями. Действуют во всех чатах: сценарии, свободный чат, агент.
        </Typography>
        <Button
          variant="contained"
          startIcon={<Plus size={15} />}
          onClick={openCreate}
          sx={{
            ml: 'auto',
            flexShrink: 0,
            bgcolor: 'var(--color-primary)',
            fontSize: 13,
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'none',
            px: 2,
            '&:hover': { bgcolor: '#4a4de0', boxShadow: 'none' },
          }}
        >
          Добавить политику
        </Button>
      </Box>

      {/* Список политик (FR-5.1) */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {isLoading &&
          Array.from({ length: 2 }).map((_, i) => (
            <Skeleton key={i} height={84} variant="rounded" />
          ))}
        {!isLoading &&
          policies.map((policy) => (
            <Box
              key={policy.id}
              sx={{
                background: '#FFFFFF',
                border: '1px solid',
                borderColor: policy.enabled ? 'rgba(5,150,105,0.30)' : 'rgba(20,116,255,0.10)',
                boxShadow: '0 2px 16px rgba(20,116,255,0.08), 0 1px 4px rgba(0,0,0,0.03)',
                borderRadius: 'var(--radius-lg)',
                p: 2.25,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 2,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 750 }}>{policy.title}</Typography>
                  {policy.enabled && (
                    <Box
                      sx={{
                        px: 1,
                        py: '2px',
                        borderRadius: '6px',
                        fontSize: 10.5,
                        fontWeight: 700,
                        bgcolor: 'rgba(5,150,105,0.10)',
                        color: '#059669',
                      }}
                    >
                      Активна
                    </Box>
                  )}
                </Box>
                <Typography sx={{ fontSize: 12.5, color: 'var(--color-text-secondary)', mb: 1 }}>
                  {policy.description}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: 'var(--color-text-muted)',
                    fontStyle: 'italic',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  «{policy.prompt}»
                </Typography>
                <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)', mt: 0.75 }}>
                  Изменена: {formatUpdatedAt(policy.updatedAt)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                <Box
                  component="button"
                  aria-label="Редактировать политику"
                  onClick={() => openEdit(policy)}
                  sx={{
                    width: 30,
                    height: 30,
                    border: 'none',
                    bgcolor: 'transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 'var(--radius-sm)',
                    color: 'var(--color-text-muted)',
                    '&:hover': { bgcolor: 'rgba(20,116,255,0.07)', color: 'var(--color-primary)' },
                  }}
                >
                  <Pencil size={14} />
                </Box>
                <Box
                  component="button"
                  aria-label="Удалить политику"
                  onClick={() => void remove(policy)}
                  sx={{
                    width: 30,
                    height: 30,
                    border: 'none',
                    bgcolor: 'transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 'var(--radius-sm)',
                    color: 'var(--color-text-muted)',
                    '&:hover': { bgcolor: 'rgba(239,68,68,0.08)', color: '#ef4444' },
                  }}
                >
                  <Trash2 size={14} />
                </Box>
                <Switch
                  size="small"
                  checked={policy.enabled}
                  onChange={() => void toggle(policy)}
                  inputProps={{ 'aria-label': `Включить ${policy.title}` }}
                />
              </Box>
            </Box>
          ))}
      </Box>

      {/* Drawer создания/редактирования (FR-5.2) */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        slotProps={{ paper: { sx: { width: { xs: '100vw', sm: 460 }, p: 3 } } }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2.5,
          }}
        >
          <Typography sx={{ fontSize: 16, fontWeight: 750 }}>
            {editingId ? 'Редактирование политики' : 'Новая политика'}
          </Typography>
          <Box
            component="button"
            aria-label="Закрыть"
            onClick={() => setDrawerOpen(false)}
            sx={{
              width: 30,
              height: 30,
              border: 'none',
              bgcolor: 'transparent',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 'var(--radius-sm)',
              color: 'var(--color-text-muted)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' },
            }}
          >
            <X size={16} />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            size="small"
            label="Название"
            value={title}
            sx={fieldSx}
            onChange={(e) => setTitle(e.target.value)}
          />
          <TextField
            size="small"
            label="Краткое описание"
            value={description}
            sx={fieldSx}
            onChange={(e) => setDescription(e.target.value)}
          />
          <TextField
            size="small"
            label="Текст промпта"
            value={prompt}
            sx={fieldSx}
            multiline
            minRows={6}
            placeholder="Инструкция для модели, которая будет добавляться к каждому запросу…"
            onChange={(e) => setPrompt(e.target.value)}
          />
          <Button
            variant="contained"
            onClick={() => void submit()}
            disabled={saving || !title.trim() || !prompt.trim()}
            startIcon={saving ? <CircularProgress size={13} color="inherit" /> : null}
            sx={{
              bgcolor: 'var(--color-primary)',
              fontSize: 13,
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 'var(--radius-md)',
              boxShadow: 'none',
              py: 1.1,
              '&:hover': { bgcolor: '#4a4de0', boxShadow: 'none' },
            }}
          >
            {saving ? 'Сохранение…' : 'Сохранить политику'}
          </Button>
        </Box>
      </Drawer>
    </Box>
  );
}
